import React, { useState, useEffect, useCallback } from "react";
import "./AgricultureSummary.css";

const AgricultureSummary = ({ isDistrictView = false, isStateView = false, districtName = '', stateName = '' }) => {
  const [crops, setCrops] = useState([]);
  const [userContext, setUserContext] = useState({ type: null, state: null, district: null }); // Initialize with null to detect loading state
  const [isLoading, setIsLoading] = useState(true);

  // Get user context from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        console.log('AgricultureSummary: User data loaded:', user); // Debug log
        if (user.email === 'state@gmail.com' && user.selectedState) {
          setUserContext({
            type: 'state',
            state: user.selectedState,
            district: null
          });
          console.log('AgricultureSummary: Set state context:', user.selectedState); // Debug log
        } else if (user.email === 'district@gmail.com' && user.selectedState && user.selectedDistrict) {
          setUserContext({
            type: 'district',
            state: user.selectedState,
            district: user.selectedDistrict
          });
          console.log('AgricultureSummary: Set district context:', user.selectedDistrict, user.selectedState); // Debug log
        } else {
          setUserContext({ type: 'general', state: null, district: null });
          console.log('AgricultureSummary: Set general context'); // Debug log
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
        setUserContext({ type: 'general', state: null, district: null });
      }
    } else {
      console.log('AgricultureSummary: No user data found'); // Debug log
      setUserContext({ type: 'general', state: null, district: null });
    }
  }, []);

  // Calculate effective values based on user context and props
  const effectiveIsDistrictView = userContext.type === 'district' ? true : isDistrictView;
  const effectiveDistrictName = userContext.type === 'district' ? userContext.district : districtName;
  const effectiveStateName = userContext.type === 'state' ? userContext.state : 
                            userContext.type === 'district' ? userContext.state : 
                            stateName;

  // Helper function to display states based on context
  const getStatesDisplay = useCallback((details) => {
    if (userContext.type === 'district' && userContext.district && userContext.state) {
      return `${userContext.district}, ${userContext.state}`;
    } else if (userContext.type === 'state' && userContext.state) {
      return userContext.state;
    } else if (effectiveIsDistrictView && effectiveDistrictName && effectiveStateName) {
      return `${effectiveDistrictName}, ${effectiveStateName}`;
    } else if (effectiveStateName && !effectiveIsDistrictView) {
      return effectiveStateName;
    } else {
      return details.states && details.states.length > 0 
        ? details.states.slice(0, 3).join(", ") + (details.states.length > 3 ? "..." : "") 
        : "Multiple States";
    }
  }, [userContext, effectiveIsDistrictView, effectiveDistrictName, effectiveStateName]);

  useEffect(() => {
    const apiUrl = (isDistrictView || isStateView)
      ? (isDistrictView 
          ? `${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001")))}/api/district-crops?district=${encodeURIComponent(districtName)}&state=${encodeURIComponent(stateName)}`
          : `${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001")))}/api/state-crops?state=${encodeURIComponent(stateName)}`)
      : `${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001"))}/api/india-crops`;
      
    fetch(apiUrl)
      .then((response) => {
        console.log('AgricultureSummary: API response status:', response.status); // Debug log
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        console.log('AgricultureSummary: API data received:', data); // Debug log
        const dynamicCrops = Object.entries(data.cropDetails || {})
          .map(([cropName, details]) => ({
            name: cropName,
            states: (isDistrictView || isStateView)
              ? (isDistrictView ? `${districtName}, ${stateName}` : stateName)
              : (details.states.length > 0 ? details.states.slice(0, 3).join(", ") + (details.states.length > 3 ? "..." : "") : "Multiple States"),
            production: `${details.production.toFixed(1)} MT`,
            area: `${details.area.toFixed(1)} thousand hectares`,
            yield: `${details.yield.toFixed(2)} MT/hectare`,
            water: details.waterSource,
            season: details.season,
            ndvi: details.ndvi,
            moisture: details.moisture,
            lastUpdate: details.lastUpdate,
            health: details.health,
          }))
          .sort((a, b) => parseFloat(b.production) - parseFloat(a.production))
          .slice(0, 10); // Show top 10 crops
        
        console.log('AgricultureSummary: Processed crops:', dynamicCrops); // Debug log
        setCrops(dynamicCrops);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("AgricultureSummary: Error fetching crop data:", error);
        // Fallback data in case of error
        const fallbackCrops = [
          {
            name: "Rice",
            states: (isDistrictView || isStateView) 
              ? (isDistrictView ? `${districtName}, ${stateName}` : stateName)
              : "West Bengal, Uttar Pradesh, Punjab",
            production: "120.0 MT",
            area: "44000.0 thousand hectares",
            yield: "2.73 MT/hectare",
            water: "Irrigated",
            season: "Kharif",
            ndvi: "0.65",
            moisture: "45.2%",
            lastUpdate: new Date().toLocaleDateString('en-IN'),
            health: "Good"
          }
        ];
        console.log('AgricultureSummary: Using fallback data:', fallbackCrops); // Debug log
        setCrops(fallbackCrops);
        setIsLoading(false);
      });
  }, [userContext, effectiveIsDistrictView, effectiveDistrictName, effectiveStateName, getStatesDisplay]);

  // Helper function to get title based on context
  const getTitle = () => {
    if (userContext.type === 'state' && userContext.state) {
      return `🌱 Agriculture Summary in ${userContext.state} State`;
    } else if (userContext.type === 'district' && userContext.district && userContext.state) {
      return `🌱 Agriculture Summary in ${userContext.district} District`;
    } else if (effectiveIsDistrictView && effectiveDistrictName) {
      return `🌱 Agriculture Summary in ${effectiveDistrictName} District`;
    } else if (effectiveStateName && !effectiveIsDistrictView) {
      return `🌱 Agriculture Summary in ${effectiveStateName} State`;
    } else {
      return '🌱 Agriculture Summary in India';
    }
  };

  // Helper function to get description based on context
  const getDescription = () => {
    if (userContext.type === 'state' && userContext.state) {
      return `Comprehensive overview of top cultivated crops in ${userContext.state} state with production data, satellite metrics, and regional distribution`;
    } else if (userContext.type === 'district' && userContext.district) {
      return `Comprehensive overview of top cultivated crops in ${userContext.district} district with production data, satellite metrics, and local distribution`;
    } else if (effectiveIsDistrictView) {
      return 'Comprehensive overview of top cultivated crops with production data, satellite metrics, and district-level distribution';
    } else if (effectiveStateName) {
      return 'Comprehensive overview of top cultivated crops with production data, satellite metrics, and state-level distribution';
    } else {
      return 'Comprehensive overview of top cultivated crops with production data, satellite metrics, and regional distribution';
    }
  };

  return (
    <div className="wrapper">
      <h2>{getTitle()}</h2>
      <p className="description">
        {getDescription()}
      </p>

      {/* Debug info - remove in production */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          backgroundColor: '#e3f2fd',
          border: '1px solid #2196f3',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '20px',
          fontSize: '14px',
          color: '#1976d2'
        }}>
          <strong>Debug Info:</strong> User Type: {userContext.type || 'loading'} 
          {userContext.state && ` | State: ${userContext.state}`}
          {userContext.district && ` | District: ${userContext.district}`}
        </div>
      )}

      {isLoading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '60px 20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '12px',
          border: '2px dashed #dee2e6'
        }}>
          <div style={{
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '16px',
              animation: 'spin 2s linear infinite'
            }}>
              🌾
            </div>
            <div style={{
              fontSize: '18px',
              color: '#6c757d',
              marginBottom: '8px'
            }}>
              Loading Agricultural Data...
            </div>
            <div style={{
              fontSize: '14px',
              color: '#adb5bd'
            }}>
              {userContext.type === 'state' && userContext.state ? 
                `Fetching data for ${userContext.state} state` :
                'Fetching crop information'
              }
            </div>
          </div>
        </div>
      ) : (
        <table className="crop-table">
          <thead>
            <tr>
              <th>Crop</th>
              <th>States</th>
              <th>Production</th>
              <th>Area</th>
              <th>Yield</th>
              <th>Water</th>
              <th>Season</th>
              <th>NDVI</th>
              <th>Soil Moisture</th>
              <th>Last Update</th>
              <th>Crop Health</th>
            </tr>
          </thead>
          <tbody>
            {crops.map((crop, idx) => (
              <tr key={idx} className={idx % 2 === 0 ? "row-even" : "row-odd"}>
                <td>{crop.name}</td>
                <td>{crop.states}</td>
                <td>{crop.production}</td>
                <td>{crop.area}</td>
                <td>{crop.yield}</td>
                <td>{crop.water}</td>
                <td>
                  <span className={`season-badge ${crop.season?.toLowerCase() || 'unknown'}`}>
                    {crop.season}
                  </span>
                </td>
                <td>{crop.ndvi}</td>
                <td>{crop.moisture}</td>
                <td>{crop.lastUpdate}</td>
                <td className={`health-${crop.health?.toLowerCase() || 'unknown'}`}>
                  {crop.health}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AgricultureSummary;